import { cn } from '@shared';

interface SkeletonProps {
  className?: string;
}

export function Skeleton({ className }: SkeletonProps) {
  return (
    <div
      className={cn('animate-pulse rounded-md bg-muted', className)}
    />
  );
}

interface LoadingStateProps {
  className?: string;
  message?: string;
}

export function LoadingState({ className, message = 'Loading...' }: LoadingStateProps) {
  return (
    <div
      className={cn('flex flex-col items-center justify-center p-8', className)}
      role="status"
      aria-live="polite"
      data-testid="loading-state"
    >
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
      <p className="mt-4 text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

export function TableSkeleton({ rows = 5, columns = 6 }: { rows?: number; columns?: number }) {
  return (
    <div className="space-y-3" data-testid="table-skeleton" role="status" aria-label="Loading table">
      <div className="flex gap-4">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={`head-${i}`} className="h-4 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex gap-4">
          {Array.from({ length: columns }).map((__, colIndex) => (
            <Skeleton key={`cell-${rowIndex}-${colIndex}`} className="h-10 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

export function CardSkeleton() {
  return (
    <div
      className="space-y-3 rounded-lg border p-4"
      data-testid="card-skeleton"
      role="status"
      aria-label="Loading card"
    >
      <Skeleton className="h-4 w-1/3" />
      <Skeleton className="h-8 w-1/2" />
      <Skeleton className="h-3 w-2/3" />
    </div>
  );
}

export function TimelineSkeleton() {
  // Skeleton for the vis-timeline area: row labels on the left, time grid on the right.
  return (
    <div
      className="overflow-hidden rounded-md border bg-background"
      role="status"
      aria-label="Loading timeline"
      data-testid="timeline-skeleton"
    >
      <div className="flex">
        {/* Row labels column */}
        <div className="flex w-40 shrink-0 flex-col gap-3 border-r bg-surface/40 p-3">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-5 w-28" />
        </div>
        {/* Time grid */}
        <div className="relative flex-1 p-3">
          <div className="flex h-6 items-center gap-2 border-b">
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-3 w-12" />
          </div>
          <div className="mt-6 space-y-4">
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-6 w-3/5" />
          </div>
        </div>
      </div>
    </div>
  );
}
